import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import clsx from 'clsx'
import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import CSSTransition from 'react-transition-group/CSSTransition'
import { useClickAway } from 'react-use'
import styled from 'styled-components'

import { Backdrop } from '@Components/Backdrop'
import { CMSBar } from '@Components/CMS/Bar/CMSBar'
import { CookieBar } from '@Components/CookieBar'
import { EasterBar } from '@Components/EasterBar'
import { DelayWarningBar } from '@Components/Header/DelayWarning'
import { DesktopSearchResults } from '@Components/Search/Desktop'
import { MobileSearchResults } from '@Components/Search/Mobile'
import { StickyTopBar } from '@Components/StickyTopBar'
import { ValentinesBar } from '@Components/ValentinesBar'
import { YOLOBar } from '@Components/YOLOBar'
import { MetadataContext } from '@Globals/Metadata'
import { useGroupVisibilityUpdater } from '@Globals/ScrollPosition'
import { SearchContext } from '@Globals/Search/Context'
import {
  showFloatingMobileHeader,
  showFloatingMobileHeaderClasses,
} from '@Globals/Style/transitions'
import { useMediaQuery } from '@Globals/useMediaQuery'

import { MainBar } from './MainBar'
import { Menu } from './Menu'
import { USPBar, USPBarProps } from './USPBar'

const defaultHeaderState = {
  backdrops: {
    menu: false,
    search: false,
  },
  showBackdrop: false,
}

const uspBarSpacing = (storeID: string): USPBarProps['evenSpacingOnSmallDesktop'] => {
  switch (storeID) {
    case '6':
    case '9':
    case '10':
    case '12':
    case '14':
    case '15':
    case '17':
    case '18':
    case '19':
    case '20':
    case '22':
    case '23':
    case '24':
    case '34':
    case '36':
      return true
    default:
      return false
  }
}

const uspBarSmallTextDesktop = (storeID: string): USPBarProps['smallTextOnSmallDesktop'] => {
  switch (storeID) {
    case '6':
    case '10':
    case '12':
    case '14':
    case '16':
    case '17':
    case '20':
    case '22':
    case '23':
    case '34':
    case '36':
      return true
    default:
      return false
  }
}

const uspBarSmallTextBelowPhablet = (
  storeID: string,
): USPBarProps['smallTextOnSmallDesktop'] => {
  switch (storeID) {
    case '6':
    case '10':
    case '12':
    case '17':
    case '20':
    case '23':
    case '34':
      return true
    default:
      return false
  }
}

const scrollStateDefault = {
  isMenuVisible: false,
  overThreshold: false,
  threshold: 0,
}

type ScrollVariables = {
  direction: 'down' | 'up'
  downStart?: number
  lastDirection: 'down' | 'up'
  upStart?: number
}

const scrollVariables: ScrollVariables = {
  direction: 'down',
  lastDirection: 'up',
}

const floatingHeaderFixedHeight = 86

interface HeaderWrapperProps {
  color?: string
  height: number
}

const HeaderWrapper = styled.div<HeaderWrapperProps>`
  .mobileSpacing {
    display: none;
    height: ${({ height }) => `${height}px`};

    &.show {
      display: block;
    }
  }

  .header {
    position: relative;
    z-index: ${({ theme }) => theme.zIndexes.header};
    width: 100%;
    background-color: ${({ color, theme }) => color ?? theme.colors.white};

    &.isFloating {
      position: fixed;
      top: -200px;
      left: 0;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    &.isFloatingVisible {
      top: 0;
    }
  }

  ${showFloatingMobileHeader}
`

export const Header = forwardRef((props, ref) => {
  const { store } = useContext(MetadataContext)
  const [
    {
      backdrops,
      showBackdrop,
    },
    setHeaderState,
  ] = useState(defaultHeaderState)
  const headerRef = useRef<HTMLDivElement>(null)
  const isDesktop = useMediaQuery('(min-width:1024px)')
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { inputHasFocus, setActive: setSearchIsActive } = useContext(SearchContext)

  const [
    {
      isMenuVisible,
      overThreshold,
      threshold,
    },
    setScrollState,
  ] = useState(scrollStateDefault)

  const calculateThreshold = useCallback(() => {
    const { height } = headerRef.current?.getBoundingClientRect() || { height: 0 }

    setScrollState(prev => ({ ...prev, threshold: height }))
  }, [])

  useEffect(() => {
    calculateThreshold()
  }, [calculateThreshold, headerRef])

  const onBackdropChange = useCallback((placement: string, isOpen: boolean) => {
    setHeaderState((prev) => {
      const newBackdrops = {
        ...prev.backdrops,
        [placement]: isOpen,
      }

      return {
        ...prev,
        backdrops: newBackdrops,
        showBackdrop: newBackdrops.menu || newBackdrops.search,
      }
    })
  }, [])

  const onMenuBackdropChange = useCallback((isOpen: boolean) => {
    onBackdropChange('menu', isOpen)
  }, [onBackdropChange])

  const onSearchBackdropChange = useCallback((isOpen: boolean) => {
    onBackdropChange('search', isOpen)
  }, [onBackdropChange])

  useScrollPosition(
    ({ currPos, prevPos }) => {
      if (isDesktop) {
        // If you happened to resize the page to a mobile'ish size, scrolled enough down to hide the header, and then
        // resized to a desktopy'y size, the header would've stayed hidden. We need a bit of cleanup in that case.
        if (overThreshold) {
          setScrollState(prev => ({ ...prev, overThreshold: false }))
        }
        return
      }

      const direction = currPos.y < prevPos.y ? 'down' : 'up'
      const overThresholdNew = overThreshold
        ? threshold - floatingHeaderFixedHeight + currPos.y <= 0
        : threshold + 100 + currPos.y <= 0

      setScrollState(prev => ({ ...prev, overThreshold: overThresholdNew }))

      if (direction === 'down') {
        if (overThresholdNew) {
          scrollVariables.upStart = currPos.y
        }

        if (currPos.y - (scrollVariables.downStart || 0) <= -20) {
          setScrollState(prev => ({ ...prev, isMenuVisible: false }))
          return
        }

        return
      }

      if (overThreshold && direction === 'up') {
        scrollVariables.downStart = currPos.y

        if (scrollVariables.upStart && scrollVariables.upStart - currPos.y <= -20) {
          setScrollState(prev => ({ ...prev, isMenuVisible: true }))
          return
        }

        return
      }
    },
    [isDesktop, overThreshold, threshold],
    undefined,
    undefined,
    250,
  )

  useClickAway(wrapperRef, () => {
    if (!inputHasFocus) {
      setSearchIsActive(false)
    }
  })

  return (
    <HeaderWrapper height={threshold} ref={ref as MutableRefObject<HTMLDivElement>}>
      {/* Mega menu backdrop */}
      <Backdrop isShowing={showBackdrop} type="behind-header" />

      <CookieBar />
      {/* Mobile spacing */}
      <div
        className={clsx(
          'mobileSpacing',
          overThreshold && 'show',
        )}
      />

      <CSSTransition
        classNames={showFloatingMobileHeaderClasses()}
        in={isMenuVisible}
        timeout={{ enter: 125, exit: 0 }}
      >
        {/* Site header */}
        <header
          className={clsx({
            header: true,
            isFloating: overThreshold,
            isFloatingVisible: overThreshold && isMenuVisible,
          })}
          ref={headerRef}
        >
          <CMSBar placement="top" />
          <ValentinesBar />
          <StickyTopBar onVisibilityChange={calculateThreshold} />
          <EasterBar />
          <YOLOBar onVisibilityChange={calculateThreshold} />
          <DelayWarningBar />
          <USPBar
            evenSpacingOnSmallDesktop={uspBarSpacing(store?.id)}
            smallTextBelowPhablet={uspBarSmallTextBelowPhablet(store?.id)}
            smallTextOnSmallDesktop={uspBarSmallTextDesktop(store?.id)}
          />
          <MainBar hide={overThreshold} />
          <Menu
            blocked={backdrops.search}
            onBackdropChange={onMenuBackdropChange}
          />
        </header>
      </CSSTransition>

      <div ref={wrapperRef}>
        <DesktopSearchResults onBackdropChange={onSearchBackdropChange} />
        <MobileSearchResults />
      </div>
    </HeaderWrapper>
  )
})

export const HeaderWithGroupVisibility = () => {
  const ref = useGroupVisibilityUpdater('header')

  return useMemo(() => <Header ref={ref} />, [ref])
}
